import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AddWhiteLabelApiResponse,
  AddWhiteLabelsApiResponse,
  AddWhiteLabelResponse,
  createAddWhiteLabel,
  getAllAddWhiteLabels,
  getMyAddWhiteLabel,
  updateMyAddWhiteLabel,
  deleteMyAddWhiteLabel,
} from "./addWhiteLabelAction";

interface AddWhiteLabelState {
  whiteLabel: AddWhiteLabelResponse | null;
  whiteLabels: AddWhiteLabelResponse[];
  total: number;
  page: number;
  pages: number;
  loading: boolean;
  error: string | null;
}

const initialState: AddWhiteLabelState = {
  whiteLabel: null,
  whiteLabels: [],
  total: 0,
  page: 1,
  pages: 1,
  loading: false,
  error: null,
};

const addWhiteLabelSlice = createSlice({
  name: "addWhiteLabel",
  initialState,
  reducers: {
    resetAddWhiteLabelState: (state) => {
      state.whiteLabel = null;
      state.whiteLabels = [];
      state.total = 0;
      state.page = 1;
      state.pages = 1;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle createAddWhiteLabel
      .addCase(createAddWhiteLabel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createAddWhiteLabel.fulfilled,
        (state, action: PayloadAction<AddWhiteLabelApiResponse>) => {
          state.loading = false;
          state.whiteLabel = action.payload.data?.whiteLabel || null;
          state.error = null;
        }
      )
      .addCase(
        createAddWhiteLabel.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            action.payload || "Failed to create white label configuration";
        }
      )
      // Handle getAllAddWhiteLabels
      .addCase(getAllAddWhiteLabels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getAllAddWhiteLabels.fulfilled,
        (state, action: PayloadAction<AddWhiteLabelsApiResponse>) => {
          state.loading = false;
          state.whiteLabels = action.payload.data?.whiteLabels || [];
          state.total = action.payload.data?.total || 0;
          state.page = action.payload.data?.page || 1;
          state.pages = action.payload.data?.pages || 1;
          state.error = null;
        }
      )
      .addCase(
        getAllAddWhiteLabels.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            action.payload || "Failed to retrieve white label configurations";
        }
      )
      // Handle getMyAddWhiteLabel
      .addCase(getMyAddWhiteLabel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getMyAddWhiteLabel.fulfilled,
        (state, action: PayloadAction<AddWhiteLabelApiResponse>) => {
          state.loading = false;
          state.whiteLabel = action.payload.data?.whiteLabel || null;
          state.error = null;
        }
      )
      .addCase(
        getMyAddWhiteLabel.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            action.payload || "Failed to retrieve white label configuration";
        }
      )
      // Handle updateMyAddWhiteLabel
      .addCase(updateMyAddWhiteLabel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateMyAddWhiteLabel.fulfilled,
        (state, action: PayloadAction<AddWhiteLabelApiResponse>) => {
          state.loading = false;
          state.whiteLabel = action.payload.data?.whiteLabel || null;
          state.error = null;
        }
      )
      .addCase(
        updateMyAddWhiteLabel.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            action.payload || "Failed to update white label configuration";
        }
      )
      // Handle deleteMyAddWhiteLabel
      .addCase(deleteMyAddWhiteLabel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteMyAddWhiteLabel.fulfilled,
        (state, action: PayloadAction<AddWhiteLabelApiResponse>) => {
          state.loading = false;
          state.whiteLabel = null;
          state.error = null;
        }
      )
      .addCase(
        deleteMyAddWhiteLabel.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            action.payload || "Failed to delete white label configuration";
        }
      );
  },
});

export const { resetAddWhiteLabelState } = addWhiteLabelSlice.actions;
export default addWhiteLabelSlice.reducer;
